import {noop} from 'es-toolkit/compat'
import {useFormik} from 'formik'
import invariant from 'invariant'

import {useAppDispatch, useAppSelector} from '../../../hooks/redux'
import {closeModal} from '../../../redux/features/modal'

import preventDoubleClick from '../../../../../commons/src/services/prevent-double-click'
import * as optionApi from '../../../services/api/option'
import {formDataToProductionTimeOptionRequest} from '../../forms/production-times/utils'

import {DefaultProductionTimes, ModalMeta, Option} from '../../../types'

import Button from '../../../../../commons/src/components/button'
import Headline from '../../../../../commons/src/components/headline'
import Modal from '../../../../../commons/src/components/modal'

import ProductionTimesForm, {
  SingleFormData,
  createValidationSchemaWithDefaultProductionTimes,
  initialValues,
} from '../../forms/production-times/form'

type Props = {
  meta: ModalMeta
  defaultProductionTimes?: DefaultProductionTimes
  linkedConfigsCount: number
  onDone?: (productionTime: Option) => void
}

const CreateProductionTimeModal = ({
  meta,
  defaultProductionTimes,
  linkedConfigsCount,
  onDone = noop,
}: Props) => {
  const dispatch = useAppDispatch()
  const provider = useAppSelector(state => state.core.provider)
  invariant(provider, 'Provider is missing.')

  const formik = useFormik<SingleFormData>({
    initialValues,
    validationSchema: createValidationSchemaWithDefaultProductionTimes(defaultProductionTimes),
    onSubmit: async (values, actions) => {
      const productionTimeOptionRequest = formDataToProductionTimeOptionRequest(values, provider)

      try {
        const createdProductionOption = await optionApi.create(productionTimeOptionRequest)

        dispatch(closeModal())
        onDone(createdProductionOption)
      } catch (error) {
        actions.setFieldError('formikGeneral', (error as Error).message)
      }
    },
  })

  return (
    <Modal
      fullscreenMobile={meta.fullscreenMobile}
      headline={<Headline label="Create new production time option" size="l" />}
      onClose={() => dispatch(closeModal())}
      buttons={
        <>
          <Button hierarchy="ghost" onClick={() => dispatch(closeModal())}>
            Cancel
          </Button>
          <Button
            disabled={
              formik.isSubmitting ||
              (formik.submitCount > 0 && Object.keys(formik.errors).length > 0)
            }
            onClick={preventDoubleClick(() => formik.handleSubmit())}
          >
            Save
          </Button>
        </>
      }
    >
      <ProductionTimesForm
        formik={formik}
        provider={provider}
        linkedConfigsCount={linkedConfigsCount}
      />
    </Modal>
  )
}

export default CreateProductionTimeModal
